// The ORCA scroll rail everywhere a pointer scrolls — the landing's own
// side runner (src/js/scroll-rail.js + scroll-rail.css): a hair-thin line, the
// travelled part lit, a glowing dot you can drag, a click on the line jumps,
// the keys move it. The page gets the landing's fixed rail on the right; every
// inner scrolling panel gets the same rail in its own gutter (the content keeps
// its room, nothing lies on a row), shown only while the panel really scrolls
// and is not covered by another layer. The native bars are hidden where a rail
// stands. Touch screens keep their native scrolling (no rail at all).
import { useEffect } from 'react';
import i18n from '@/i18n';

/** inner panels that scroll vertically (each must keep a right gutter — 24-scroll.css) */
export const RAIL_PANELS = ['.calv-list', '.ask-body', '.conv-thread', '.cs-hist', '.cs-thread', '.sheet-body', '.bn-list', '.gsh-body', '.drawer', '.timeline .ev-card'];
const FINE = '(hover: hover) and (pointer: fine)';

type Rail = { el: HTMLElement; rail: HTMLDivElement; fill: HTMLDivElement; dot: HTMLDivElement; page: boolean };

function makeRail(page: boolean): Pick<Rail, 'rail' | 'fill' | 'dot'> {
  const rail = document.createElement('div');
  rail.className = page ? 'scroll-rail orail-page' : 'scroll-rail orail';
  rail.setAttribute('role', 'scrollbar');
  rail.setAttribute('aria-orientation', 'vertical');
  rail.setAttribute('aria-valuemin', '0');
  rail.setAttribute('aria-valuemax', '100');
  rail.tabIndex = page ? 0 : -1;
  rail.setAttribute('aria-label', page ? i18n.t('common:scroll.page') : i18n.t('common:scroll.panel'));
  const track = document.createElement('div'); track.className = 'scroll-rail-track';
  const fill = document.createElement('div'); fill.className = 'scroll-rail-fill';
  const dot = document.createElement('div'); dot.className = 'scroll-rail-dot';
  rail.append(track, fill, dot);
  document.body.appendChild(rail);
  return { rail, fill, dot };
}

export function ScrollRails() {
  useEffect(() => {
    const mq = window.matchMedia(FINE);
    if (!mq.matches) return undefined;
    document.documentElement.classList.add('has-rails');
    const rails = new Map<HTMLElement, Rail>();
    const pageEl = document.scrollingElement as HTMLElement;

    const max = (r: Rail) => Math.max(0, r.el.scrollHeight - r.el.clientHeight);
    const top = (r: Rail) => (r.page ? window.scrollY : r.el.scrollTop);
    const setTop = (r: Rail, v: number, smooth = false) => {
      const t = Math.min(max(r), Math.max(0, v));
      if (r.page) window.scrollTo({ top: t, behavior: smooth ? 'smooth' : 'auto' }); else r.el.scrollTo({ top: t, behavior: smooth ? 'smooth' : 'auto' });
    };

    const place = (r: Rail) => {
      const m = max(r);
      if (r.page) {
        r.rail.style.display = m > 4 ? '' : 'none';
      } else {
        const rect = r.el.getBoundingClientRect();
        const vTop = Math.max(rect.top, 0), vBottom = Math.min(rect.bottom, window.innerHeight);
        let show = m > 4 && rect.width > 40 && vBottom - vTop > 60 && getComputedStyle(r.el).visibility !== 'hidden';
        if (show) {
          // covered by another layer (a sheet, a menu)? then its own rail speaks, not this one
          const x = rect.right - 8, y = (vTop + vBottom) / 2;
          const hit = document.elementsFromPoint(x, y).find((n) => !(n as HTMLElement).classList?.contains('scroll-rail') && !(n as HTMLElement).closest?.('.scroll-rail'));
          show = !!hit && (hit === r.el || r.el.contains(hit));
        }
        r.rail.style.display = show ? '' : 'none';
        if (!show) return;
        r.rail.style.left = `${rect.right - 15}px`;
        r.rail.style.top = `${vTop + 8}px`;
        r.rail.style.height = `${Math.max(40, vBottom - vTop - 16)}px`;
      }
      const pct = m > 0 ? Math.min(100, Math.max(0, (top(r) / m) * 100)) : 0;
      r.fill.style.height = `${pct.toFixed(1)}%`;
      r.dot.style.top = `${pct.toFixed(1)}%`;
      r.rail.setAttribute('aria-valuenow', String(Math.round(pct)));
    };

    const attach = (el: HTMLElement, page = false) => {
      if (rails.has(el)) return;
      const parts = makeRail(page);
      const r: Rail = { el, page, ...parts };
      rails.set(el, r);
      let dragging = false;
      const toPointer = (clientY: number) => {
        const rect = r.rail.getBoundingClientRect();
        setTop(r, max(r) * Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)));
      };
      const finish = (e: PointerEvent) => {
        if (!dragging) return;
        dragging = false;
        r.rail.classList.remove('is-dragging');
        document.documentElement.classList.remove('scroll-rail-dragging');
        if (r.rail.hasPointerCapture?.(e.pointerId)) r.rail.releasePointerCapture(e.pointerId);
      };
      r.rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        dragging = true;
        r.rail.classList.add('is-dragging');
        document.documentElement.classList.add('scroll-rail-dragging');
        r.rail.setPointerCapture?.(e.pointerId);
        toPointer(e.clientY);
      });
      r.rail.addEventListener('pointermove', (e) => { if (dragging) { e.preventDefault(); toPointer(e.clientY); } });
      r.rail.addEventListener('pointerup', finish);
      r.rail.addEventListener('pointercancel', finish);
      r.rail.addEventListener('keydown', (e) => {
        const h = r.page ? window.innerHeight : r.el.clientHeight;
        const line = Math.max(72, h * 0.08), pageStep = h * 0.82, cur = top(r);
        const to = e.key === 'ArrowDown' ? cur + line : e.key === 'ArrowUp' ? cur - line : e.key === 'PageDown' ? cur + pageStep
          : e.key === 'PageUp' ? cur - pageStep : e.key === 'Home' ? 0 : e.key === 'End' ? max(r) : null;
        if (to === null) return;
        e.preventDefault();
        setTop(r, to, true);
      });
      place(r);
    };

    const scan = () => {
      for (const [el, r] of rails) if (!r.page && !el.isConnected) { r.rail.remove(); rails.delete(el); }
      document.querySelectorAll<HTMLElement>(RAIL_PANELS.join(',')).forEach((el) => attach(el));
    };

    let ticking = false;
    const update = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { ticking = false; rails.forEach(place); });
    };
    attach(pageEl, true);
    scan();
    let scanT = 0;
    const mo = new MutationObserver(() => { window.clearTimeout(scanT); scanT = window.setTimeout(() => { scan(); update(); }, 120); });
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    // layout can change without a scroll (a sheet opening, a list loading): a calm re-check
    const iv = window.setInterval(update, 600);
    return () => {
      mo.disconnect();
      window.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
      window.clearInterval(iv); window.clearTimeout(scanT);
      rails.forEach((r) => r.rail.remove());
      document.documentElement.classList.remove('has-rails');
    };
  }, []);
  return null;
}
